/**
 * i18n Routing Configuration
 *
 * This file controls how language routing works across the site.
 * You can configure per-deployment defaults for multi-domain/subdomain SEO setups.
 *
 * MODES:
 * - "prefix"    → e.g. thailand-elite.com/fr/memberships
 * - "subdomain" → e.g. fr.thailand-elite.com/memberships
 *
 * DOMAIN MAPPING:
 * Use `domainMap` to assign a dedicated domain per language for hreflang SEO tags.
 * Languages without a dedicated domain will use the main domain + prefix/subdomain.
 *
 * SLUG MAP:
 * Use `slugMap` to define localized URL slugs per language.
 * Keys are React route names (English), values are per-language translations.
 * Languages with a dedicated domain use localized slugs at the root (no prefix).
 *
 * EXAMPLES:
 *   French site:  { defaultLanguage: "fr", mode: "prefix" }
 *     → thailandelite.fr/adhesions           (French memberships)
 *     → thailandelite.fr/en/memberships      (English memberships)
 *
 *   English site: { defaultLanguage: "en", mode: "prefix" }
 *     → thailand-elite.com/memberships       (English)
 *     → thailand-elite.com/fr/adhesions      (French)
 */

export type I18nMode = "prefix" | "subdomain";

/**
 * Localized slug map.
 * Key: React route name (always English, no leading slash).
 * Value: per-language slug (falls back to key if not defined).
 */
export type SlugMap = Record<string, Partial<Record<string, string>>>;

export interface I18nRoutingConfig {
  /** The default language — shown without prefix/subdomain */
  defaultLanguage: string;
  /** Routing mode: "prefix" (/fr/) or "subdomain" (fr.domain.com) */
  mode: I18nMode;
  /** All supported language codes */
  supportedLanguages: string[];
  /** Main domain for this deployment (used for canonical URLs) */
  mainDomain: string;
  /**
   * Per-language dedicated domains for hreflang SEO tags.
   * If a language has a dedicated domain, hreflang will point there (root, no prefix).
   * Languages not listed here will use mainDomain + prefix/subdomain.
   */
  domainMap?: Record<string, string>;
  /**
   * Subdomain-to-language mapping (only used in "subdomain" mode).
   */
  subdomainMap?: Record<string, string>;
  /**
   * Localized URL slugs per route name.
   * Used to build SEO-friendly URLs in each language.
   * e.g. slugMap.memberships.fr = "adhesions" → /adhesions on thailandelite.fr
   */
  slugMap?: SlugMap;
}

const config: I18nRoutingConfig = {
  defaultLanguage: "en",
  mode: "prefix",
  mainDomain: "https://thailand-elite.com",
  supportedLanguages: ["en", "fr", "zh", "de", "ja", "it", "ru"],
  domainMap: {
    fr: "https://thailandelite.fr",
  },
  subdomainMap: {
    fr: "fr",
    de: "de",
    cn: "zh",
    ja: "ja",
    it: "it",
    ru: "ru",
  },
  /**
   * Localized slugs — only define when different from the English route name.
   * English (default) slugs are always the route name itself.
   *
   * ZH and JA intentionally use English slugs (no entries below): pinyin/romaji
   * slugs are not meaningful to native speakers, and major international sites
   * (Booking, Airbnb, Agoda) keep English slugs under /zh/ and /ja/.
   * RU uses Latin transliteration so URLs survive copy-paste in non-Cyrillic
   * environments (email, chat, etc.).
   */
  slugMap: {
    memberships: {
      fr: "adhesions",
      de: "mitgliedschaften",
      it: "adesioni",
      ru: "chlenstvo",
    },
    "membership/:tier": {
      fr: "adhesion/:tier",
      de: "mitgliedschaft/:tier",
      it: "adesione/:tier",
      ru: "chlenstvo/:tier",
    },
    about: {
      fr: "a-propos",
      de: "ueber-uns",
      it: "chi-siamo",
      ru: "o-nas",
    },
    faqs: {
      fr: "faq",
    },
    news: {
      fr: "actualites",
      de: "nachrichten",
      it: "notizie",
      ru: "novosti",
    },
    "news/:slug": {
      fr: "actualites/:slug",
      de: "nachrichten/:slug",
      it: "notizie/:slug",
      ru: "novosti/:slug",
    },
    "privilege-points": {
      fr: "points-privileges",
      de: "privilege-punkte",
      it: "punti-privilegio",
      ru: "bonusnye-bally",
    },
    blog: {
      fr: "blog",
    },
    "blog/:slug": {
      fr: "blog/:slug",
    },
    privileges: {
      fr: "privileges",
      de: "privilegien",
      it: "privilegi",
      ru: "privilegii",
    },
    visa: {
      fr: "visa",
      de: "visum",
      it: "visto",
      ru: "viza",
    },
    "thailand-privilege-card": {
      fr: "carte-thailand-privilege",
      de: "thailand-privilege-card",
      it: "carta-thailand-privilege",
      ru: "thailand-privilege-card",
    },
    "how-to-apply": {
      fr: "devenir-membre",
      de: "bewerbung-anleitung",
      it: "come-candidarsi",
      ru: "kak-podat-zayavku",
    },
    "member-application-process": {
      fr: "devenir-membre",
    },
    apply: {
      fr: "adherer",
      de: "bewerben",
      it: "candidati",
      ru: "podat-zayavku",
    },
    privacy: {
      fr: "confidentialite",
      de: "datenschutz",
      ru: "konfidentsialnost",
    },
    terms: {
      fr: "conditions-utilisation",
      de: "agb",
      it: "termini-condizioni",
      ru: "usloviya",
    },
    family: {
      fr: "famille",
      de: "familie",
      it: "famiglia",
      ru: "semya",
    },
    compare: {
      fr: "comparer",
      de: "vergleichen",
      it: "confronta",
      ru: "sravnit",
    },
    guides: {
      fr: "guides",
      // /guides/ path stays English in all languages so that guide article
      // routing (/{lang}/guides/:slug) doesn't need per-language aliases.
      // Only the article slug itself is localized — see guideSlugMap.ts.
    },
    downloads: {
      fr: "telechargements",
      ru: "zagruzki",
    },
    "discontinued-memberships": {
      fr: "adhesions-arretees",
      de: "eingestellte-mitgliedschaften",
      it: "adesioni-cessate",
      ru: "zakrytye-programmy",
    },
    contact: {
      fr: "contact",
      de: "kontakt",
      it: "contatti",
      ru: "kontakty",
    },
  },
};

export default config;
